import { Link } from "react-router-dom";
import {
    ShieldCheck,
    Headphones,
    BedDouble,
    Search,
    MousePointerClick,
    CreditCard,
    Package,
    ArrowRight,
} from "lucide-react";
import InnerHero from "@/components/InnerHero";
import SearchWidget from "@/components/SearchWidget";
import Reveal from "@/components/Reveal";

const STEPS = [
    {
        n: "01",
        title: "Buscá",
        desc: "Elegí origen, destino y fecha de tu viaje.",
        icon: Search,
    },
    {
        n: "02",
        title: "Seleccioná",
        desc: "Elegí horario, servicio y asiento en la tiquetera.",
        icon: MousePointerClick,
    },
    {
        n: "03",
        title: "Comprá",
        desc: "Finalizá la compra online de forma segura.",
        icon: CreditCard,
    },
];

const TRUST = [
    { icon: ShieldCheck, label: "Pago 100% seguro" },
    { icon: Headphones, label: "Atención de 8 a 21 hs" },
    { icon: BedDouble, label: "Unidades cama y semi cama" },
];

export default function BuscarViajes() {
    return (
        <div data-testid="buscar-page">
            <InnerHero
                variant="orange"
                crumb="Comprar pasajes"
                title="Encontrá tu próximo destino"
                subtitle="Completá los datos de tu viaje y te llevamos a la tiquetera para elegir horario, asiento y pagar online."
            >
                <div className="mt-10 max-w-5xl -mb-10">
                    <SearchWidget />
                    <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3">
                        {TRUST.map((t) => (
                            <div
                                key={t.label}
                                className="flex items-center gap-2 text-sm font-medium text-white"
                            >
                                <t.icon className="h-4 w-4" />
                                {t.label}
                            </div>
                        ))}
                    </div>
                </div>
            </InnerHero>

            {/* PASOS */}
            <section className="py-20 lg:py-28">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <Reveal>
                        <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand">
                            Cómo funciona
                        </p>
                        <h2 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-ink">
                            Tres pasos y listo
                        </h2>
                    </Reveal>
                    <div className="mt-12 grid gap-5 md:grid-cols-3">
                        {STEPS.map((s, i) => (
                            <Reveal key={s.n} delay={i * 0.1}>
                                <div className="relative overflow-hidden rounded-3xl border border-black/5 bg-white p-8 shadow-sm">
                                    <div className="flex items-start justify-between">
                                        <span className="font-display text-5xl font-extrabold text-black/5">
                                            {s.n}
                                        </span>
                                        <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-brand text-white">
                                            <s.icon className="h-6 w-6" />
                                        </span>
                                    </div>
                                    <h3 className="mt-4 text-xl font-extrabold uppercase tracking-tight text-ink">
                                        {s.title}
                                    </h3>
                                    <p className="mt-2 text-sm text-ink/60">
                                        {s.desc}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* PAGO SEGURO */}
            <section className="pb-20 lg:pb-28">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <Reveal>
                        <div className="grid items-center gap-10 overflow-hidden rounded-[32px] bg-ink p-8 sm:p-12 lg:grid-cols-2">
                            <div>
                                <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand">
                                    Pago seguro
                                </p>
                                <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-white">
                                    Aceptamos todas las tarjetas
                                </h2>
                                <p className="mt-4 max-w-md text-base text-white/70">
                                    Pagá tus pasajes de forma 100% segura con
                                    Mercado Pago y todas las tarjetas de crédito y
                                    débito.
                                </p>
                            </div>
                            <div className="overflow-hidden rounded-3xl">
                                <img
                                    src="/images/buscar-image.webp"
                                    alt="Medios de pago"
                                    className="h-full w-full object-contain"
                                />
                            </div>
                        </div>
                    </Reveal>

                    <Reveal delay={0.1}>
                        <Link
                            to="/encomiendas"
                            data-testid="buscar-encomiendas-cta"
                            className="group mt-6 flex flex-col items-start justify-between gap-4 rounded-3xl border border-black/5 bg-brand-light p-8 sm:flex-row sm:items-center"
                        >
                            <div className="flex items-center gap-4">
                                <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-brand text-white">
                                    <Package className="h-6 w-6" />
                                </span>
                                <div>
                                    <h3 className="text-lg font-extrabold uppercase tracking-tight text-ink">
                                        ¿Enviás una encomienda?
                                    </h3>
                                    <p className="text-sm text-ink/60">
                                        También podés cotizar y seguir tus envíos
                                        con El Cometa Bis.
                                    </p>
                                </div>
                            </div>
                            <span className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-brand">
                                Ir a encomiendas
                                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                            </span>
                        </Link>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
